/**
 * @cfleet/auth - Better Auth Adapter
 *
 * Implements the AuthAdapter interface for Better Auth cookie-based authentication.
 * Better Auth manages sessions via cookies, with the session_data cookie containing
 * a JWT that the frontend extracts and sends in the Authorization header.
 *
 * Key characteristics:
 * - Cookie-based sessions with JWT extraction for API calls
 * - JWT extracted from better-auth.session_data cookie
 * - Automatic session refresh via cookie expiry
 * - CSRF protection for mutation endpoints
 * - Credentials: 'include' required for all requests
 */

import type {
  AuthAdapter,
  AuthAdapterResponse,
  SessionResponse,
  BetterAuthSignInResponse,
  BetterAuthSignUpResponse,
} from './types';
import type {
  LoginCredentials,
  User,
  AuthTokens,
  Organization,
  UserRole,
  UserPermissions,
  AppType,
} from '../types';
import { DEFAULT_PERMISSIONS } from '../types';
import { createBetterAuthHeaders } from '../utils/csrf';

/**
 * Normalize role string to UserRole type
 * Better Auth may return lowercase roles, frontend uses PascalCase
 */
function normalizeRole(role: string | undefined, defaultRole: UserRole = 'Dispatcher'): UserRole {
  if (!role) return defaultRole;

  const roleMap: Record<string, UserRole> = {
    admin: 'Admin',
    manager: 'Manager',
    dispatcher: 'Dispatcher',
    viewer: 'Dispatcher', // Map viewer to Dispatcher
    driver: 'Driver',
    customer: 'Customer',
    // PascalCase passthrough
    Admin: 'Admin',
    Manager: 'Manager',
    Dispatcher: 'Dispatcher',
    Driver: 'Driver',
    Customer: 'Customer',
  };

  return roleMap[role] || roleMap[role.toLowerCase()] || defaultRole;
}

/**
 * Parse organization_ids JSON string to Organization array.
 * When orgDetails is provided (from login response), uses real org names.
 * Falls back to using the org ID as the name when details are unavailable.
 */
function parseOrganizations(
  orgIdsJson: string | undefined,
  role: UserRole,
  orgDetails?: Array<{ id: string; name: string; slug: string }>
): Organization[] {
  if (!orgIdsJson) return [];

  try {
    const ids = JSON.parse(orgIdsJson) as string[];
    if (!Array.isArray(ids)) return [];

    return ids.map((id) => ({
      id,
      name: orgDetails?.find((o) => o.id === id)?.name || id,
      role,
    }));
  } catch {
    return [];
  }
}

/**
 * Parse permissions from Better Auth user response
 * Better Auth stores permissions as a JSON string, parse it to UserPermissions
 * Falls back to role-based defaults if not present
 */
function extractPermissions(permissionsJson: string | undefined, role: UserRole): UserPermissions {
  // Try to parse stored permissions first
  if (permissionsJson) {
    try {
      const parsed = JSON.parse(permissionsJson);
      if (typeof parsed === 'object' && parsed !== null) {
        return {
          viewClients: Boolean(parsed.viewClients),
          viewAudit: Boolean(parsed.viewAudit),
          viewAnalytics: Boolean(parsed.viewAnalytics),
          manageDrivers: Boolean(parsed.manageDrivers),
          manageRoutes: Boolean(parsed.manageRoutes),
          manageOrders: Boolean(parsed.manageOrders),
        };
      }
    } catch {
      // Fall through to role-based defaults
    }
  }

  // Fall back to role-based defaults
  switch (role) {
    case 'Admin':
      return {
        viewClients: true,
        viewAudit: true,
        viewAnalytics: true,
        manageDrivers: true,
        manageRoutes: true,
        manageOrders: true,
      };
    case 'Manager':
      return {
        viewClients: true,
        viewAudit: true,
        viewAnalytics: true,
        manageDrivers: true,
        manageRoutes: true,
        manageOrders: true,
      };
    case 'Dispatcher':
      return {
        viewClients: true,
        viewAudit: false,
        viewAnalytics: true,
        manageDrivers: false,
        manageRoutes: true,
        manageOrders: true,
      };
    default:
      return { ...DEFAULT_PERMISSIONS };
  }
}

/**
 * Check if a user account is active based on status
 */
function isUserActive(status: string | undefined): boolean {
  // Default to active if status not provided
  if (!status) return true;
  return status === 'Active';
}

/**
 * Extract JWT from Better Auth session_data cookie
 *
 * Better Auth stores the JWT in a cookie named 'better-auth.session_data'.
 * When the JWT is large, it may be chunked across multiple cookies:
 * - better-auth.session_data.0
 * - better-auth.session_data.1
 * - etc.
 *
 * This function handles both single and chunked cookies.
 *
 * @returns The JWT string if found, null otherwise
 */
function extractJWTFromCookie(): string | null {
  if (typeof document === 'undefined') return null;

  // Parse all cookies into a map
  const cookies = document.cookie.split(';').reduce(
    (acc, cookie) => {
      const trimmed = cookie.trim();
      const eqIndex = trimmed.indexOf('=');
      if (eqIndex > 0) {
        const name = trimmed.substring(0, eqIndex);
        const value = trimmed.substring(eqIndex + 1);
        acc[name] = value;
      }
      return acc;
    },
    {} as Record<string, string>
  );

  // Try single cookie first (most common case)
  const singleCookie = cookies['better-auth.session_data'];
  if (singleCookie) {
    try {
      return decodeURIComponent(singleCookie);
    } catch {
      return singleCookie;
    }
  }

  // Handle chunked cookies (for large JWTs)
  const chunks: string[] = [];
  let i = 0;
  while (cookies[`better-auth.session_data.${i}`]) {
    chunks.push(cookies[`better-auth.session_data.${i}`]);
    i++;
  }

  if (chunks.length > 0) {
    try {
      return decodeURIComponent(chunks.join(''));
    } catch {
      return chunks.join('');
    }
  }

  return null;
}

/**
 * Create tokens from Better Auth session
 *
 * Extracts the JWT from the session_data cookie and includes it as accessToken.
 * This allows the api-client middleware to attach it to the Authorization header.
 *
 * @param expiresAt - Session expiry timestamp from Better Auth
 * @returns AuthTokens with extracted JWT as accessToken
 */
function createTokensFromSession(expiresAt?: string, refreshToken?: string): AuthTokens {
  const jwt = extractJWTFromCookie();
  const expiry = expiresAt ? new Date(expiresAt).getTime() : Date.now() + 3600 * 1000;

  return {
    accessToken: jwt || '', // JWT from cookie for api-client middleware
    refreshToken: refreshToken || '',
    expiresIn: Math.floor((expiry - Date.now()) / 1000),
    expiresAt: expiry,
  };
}

/**
 * Better Auth Adapter
 *
 * Provides authentication operations via Better Auth sidecar.
 * Uses cookie-based sessions with CSRF protection.
 *
 * @example
 * ```ts
 * const adapter = new BetterAuthAdapter('http://localhost:8080');
 *
 * // Login
 * const response = await adapter.login({ email, password });
 *
 * // Check session
 * const session = await adapter.getSession();
 *
 * // Logout
 * await adapter.logout(null);
 * ```
 */
export class BetterAuthAdapter implements AuthAdapter {
  readonly name = 'better-auth';
  readonly usesCookies = true;

  /**
   * Create a new Better Auth adapter
   * @param baseUrl - Base URL for Better Auth endpoints (e.g., 'http://localhost:8080')
   * @param appType - App type to determine which auth endpoints to use (default: 'dispatcher')
   */
  constructor(
    private readonly baseUrl: string,
    private readonly appType: AppType = 'dispatcher'
  ) {}

  /**
   * Authenticate with email/password
   * Uses the adapter endpoint which returns tokens in response body
   * - Dispatcher: POST /api/v1/auth/login
   * - Driver (with deviceId): POST /api/v1/driver/auth/login
   * - Customer: POST /api/v1/customer/auth/login
   */
  async login(credentials: LoginCredentials): Promise<AuthAdapterResponse> {
    // Determine endpoint based on app type and whether deviceId is provided (driver app)
    const isDriverLogin = !!credentials.deviceId;

    let endpoint: string;
    if (isDriverLogin) {
      endpoint = `${this.baseUrl}/api/v1/driver/auth/login`;
    } else if (this.appType === 'customer') {
      endpoint = `${this.baseUrl}/api/v1/customer/auth/login`;
    } else {
      endpoint = `${this.baseUrl}/api/v1/auth/login`;
    }

    const body: Record<string, string> = {
      email: credentials.email,
      password: credentials.password,
    };
    if (credentials.deviceId) {
      body.device_id = credentials.deviceId;
    }
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: createBetterAuthHeaders({}, this.baseUrl),
      credentials: 'include',
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.detail ||
          errorData.message ||
          errorData.error ||
          `Login failed: ${response.status}`
      );
    }

    const data = await response.json();

    // Adapter routes return tokens directly in response body
    // Driver login: { token, refresh_token, driver: {...} }
    // Dispatcher login: { token, refresh_token, user: {...} }
    // Customer login: { token, refresh_token, customer: {...} }
    if (isDriverLogin) {
      return this.normalizeDriverLoginResponse(data);
    }

    if (this.appType === 'customer') {
      return this.normalizeCustomerLoginResponse(data);
    }

    return this.normalizeAdapterLoginResponse(data);
  }

  /**
   * Sign out and invalidate session
   * Uses POST /api/v1/auth/logout endpoint (adapter route)
   * - Dispatcher: POST /api/v1/auth/logout
   * - Driver: POST /api/v1/driver/auth/logout
   * - Customer: POST /api/v1/customer/auth/logout
   *
   * Note: Better Auth requires:
   * - Origin header for CSRF protection
   * - A request body (even if empty) to avoid 500 errors
   */
  async logout(_accessToken: string | null): Promise<void> {
    let endpoint: string;
    if (this.appType === 'driver') {
      endpoint = `${this.baseUrl}/api/v1/driver/auth/logout`;
    } else if (this.appType === 'customer') {
      endpoint = `${this.baseUrl}/api/v1/customer/auth/logout`;
    } else {
      endpoint = `${this.baseUrl}/api/v1/auth/logout`;
    }

    try {
      await fetch(endpoint, {
        method: 'POST',
        headers: createBetterAuthHeaders({}, this.baseUrl),
        credentials: 'include',
        // Better Auth requires a body (even empty) to process sign-out correctly
        body: JSON.stringify({}),
      });
    } catch {
      // Ignore logout failures - session may already be invalid
      console.warn('[BetterAuthAdapter] Logout request failed, session may already be invalid');
    }
  }

  /**
   * Refresh session using the refresh token
   * Uses POST /api/v1/auth/refresh with refresh_token in body (same as backend pattern)
   * - Dispatcher: POST /api/v1/auth/refresh
   * - Driver: POST /api/v1/driver/auth/refresh
   * - Customer: POST /api/v1/customer/auth/refresh
   * Falls back to cookie-based getSession() if no refresh token provided
   */
  async refreshSession(refreshToken: string | null): Promise<AuthAdapterResponse> {
    let refreshEndpoint: string;
    if (this.appType === 'driver') {
      refreshEndpoint = `${this.baseUrl}/api/v1/driver/auth/refresh`;
    } else if (this.appType === 'customer') {
      refreshEndpoint = `${this.baseUrl}/api/v1/customer/auth/refresh`;
    } else {
      refreshEndpoint = `${this.baseUrl}/api/v1/auth/refresh`;
    }

    // If we have a refresh token, use the adapter endpoint (works in test environments)
    if (refreshToken) {
      const response = await fetch(refreshEndpoint, {
        method: 'POST',
        headers: createBetterAuthHeaders({}, this.baseUrl),
        credentials: 'include',
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (!response.ok) {
        throw new Error('Session expired or invalid');
      }

      const data = await response.json();

      // Response format: { token, refresh_token, expires_in, organizations?: [...] }
      // Need to get user info from the session endpoint
      const session = await this.getSession();

      const refreshedUser = session?.user
        ? this.normalizeUser(session.user)
        : {
            id: '',
            email: '',
            name: '',
            role: normalizeRole(this.appType),
            permissions: { ...DEFAULT_PERMISSIONS },
            organizations: [],
            status: 'Active' as const,
          };

      // Prefer orgs from refresh response (more reliable than session for driver/customer)
      const organizations: Organization[] =
        data.organizations && data.organizations.length > 0
          ? data.organizations
          : refreshedUser.organizations || [];

      if (organizations.length > 0 && refreshedUser.organizations?.length === 0) {
        refreshedUser.organizations = organizations;
      }

      return {
        user: refreshedUser,
        tokens: {
          accessToken: data.token || '',
          refreshToken: data.refresh_token || refreshToken,
          expiresIn: data.expires_in || 604800,
          expiresAt: Date.now() + (data.expires_in || 604800) * 1000,
        },
        organizations,
      };
    }

    // Fallback to cookie-based refresh (browser environments)
    const session = await this.getSession();

    if (!session) {
      throw new Error('Session expired or invalid');
    }

    return this.normalizeSessionResponse(session);
  }

  /**
   * Get current session from cookies
   * Returns null if no valid session exists
   * - Dispatcher: GET /auth/get-session
   * - Driver: GET /api/v1/driver/auth/session
   * - Customer: GET /api/v1/customer/auth/session
   */
  async getSession(): Promise<SessionResponse | null> {
    let sessionEndpoint: string;
    if (this.appType === 'driver') {
      sessionEndpoint = `${this.baseUrl}/api/v1/driver/auth/session`;
    } else if (this.appType === 'customer') {
      sessionEndpoint = `${this.baseUrl}/api/v1/customer/auth/session`;
    } else {
      sessionEndpoint = `${this.baseUrl}/auth/get-session`;
    }

    try {
      const response = await fetch(sessionEndpoint, {
        method: 'GET',
        headers: createBetterAuthHeaders({}, this.baseUrl),
        credentials: 'include',
      });

      if (!response.ok) {
        // 401 means no session, not an error
        if (response.status === 401) {
          return null;
        }
        return null;
      }

      const data = await response.json();

      // Driver session endpoint returns { valid, driver, expires_at }
      // Normalize to SessionResponse shape for consistent downstream handling
      if (this.appType === 'driver') {
        if (!data || !data.valid || !data.driver) {
          return null;
        }
        return {
          session: {
            id: '',
            userId: data.driver.id,
            expiresAt: data.expires_at,
            createdAt: data.expires_at,
            updatedAt: data.expires_at,
          },
          user: {
            id: data.driver.id,
            name: data.driver.name,
            email: data.driver.email,
            emailVerified: true,
            createdAt: data.expires_at,
            updatedAt: data.expires_at,
            image: data.driver.avatar_url,
            role: 'driver',
            identity_type: 'driver',
            status: data.driver.status,
          },
        } as SessionResponse;
      }

      // Better Auth returns null/empty if no session
      if (!data || !data.session || !data.user) {
        return null;
      }

      return data as SessionResponse;
    } catch (error) {
      // Re-throw network/parsing errors so callers can distinguish
      // from expired sessions (which return null)
      throw error;
    }
  }

  /**
   * Register a new user account
   * Uses POST /auth/sign-up/email endpoint
   */
  async signUp(
    email: string,
    password: string,
    name: string,
    additionalData?: Record<string, unknown>
  ): Promise<AuthAdapterResponse> {
    const response = await fetch(`${this.baseUrl}/auth/sign-up/email`, {
      method: 'POST',
      headers: createBetterAuthHeaders({}, this.baseUrl),
      credentials: 'include',
      body: JSON.stringify({
        email,
        password,
        name,
        ...additionalData,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || errorData.error || `Sign up failed: ${response.status}`);
    }

    const data: BetterAuthSignUpResponse = await response.json();
    return this.normalizeAuthResponse(data);
  }

  /**
   * Request password reset email
   * Uses POST /auth/forget-password endpoint
   */
  async forgotPassword(email: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/auth/forget-password`, {
      method: 'POST',
      headers: createBetterAuthHeaders({}, this.baseUrl),
      credentials: 'include',
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || errorData.error || `Password reset request failed: ${response.status}`
      );
    }
  }

  /**
   * Reset password with token
   * Uses POST /auth/reset-password endpoint
   */
  async resetPassword(token: string, newPassword: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/auth/reset-password`, {
      method: 'POST',
      headers: createBetterAuthHeaders({}, this.baseUrl),
      credentials: 'include',
      body: JSON.stringify({
        token,
        newPassword,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || errorData.error || `Password reset failed: ${response.status}`
      );
    }
  }

  /**
   * Normalize Better Auth sign-in/sign-up response to AuthAdapterResponse
   */
  private normalizeAuthResponse(
    data: BetterAuthSignInResponse | BetterAuthSignUpResponse
  ): AuthAdapterResponse {
    const role = normalizeRole(data.user.role, 'Dispatcher');
    const organizations = parseOrganizations(data.user.organization_ids, role);

    // Check user account status
    if (!isUserActive(data.user.status)) {
      throw new Error('Account is not active. Please contact your administrator.');
    }

    const user: User = {
      id: data.user.id,
      name: data.user.name,
      email: data.user.email,
      role,
      permissions: extractPermissions(data.user.permissions, role),
      avatar: data.user.image,
      organizations: organizations.length > 0 ? organizations : undefined,
      created_at: data.user.createdAt,
    };

    // Get session expiry if available and extract JWT from cookie
    const session = 'session' in data ? data.session : undefined;
    const tokens = createTokensFromSession(session?.expiresAt);

    return {
      user,
      tokens,
      organizations,
    };
  }

  /**
   * Normalize SessionResponse to AuthAdapterResponse
   */
  private normalizeSessionResponse(
    session: SessionResponse,
    refreshToken?: string
  ): AuthAdapterResponse {
    const role = normalizeRole(session.user.role, 'Dispatcher');
    const organizations = parseOrganizations(session.user.organization_ids, role);

    // Check user account status
    if (!isUserActive(session.user.status)) {
      throw new Error('Account is not active. Please contact your administrator.');
    }

    const user: User = {
      id: session.user.id,
      name: session.user.name,
      email: session.user.email,
      role,
      permissions: extractPermissions(session.user.permissions, role),
      avatar: session.user.image,
      organizations: organizations.length > 0 ? organizations : undefined,
      created_at: session.user.createdAt,
    };

    // Extract JWT from cookie for api-client middleware
    const tokens = createTokensFromSession(session.session.expiresAt, refreshToken);

    return {
      user,
      tokens,
      organizations,
    };
  }

  /**
   * Normalize adapter login response (dispatcher/customer)
   * Response format: { token, refresh_token, expires_in, user: {...}, organizations?: [...] }
   */
  private normalizeAdapterLoginResponse(data: {
    token: string;
    refresh_token: string;
    expires_in: number;
    user: {
      id: string;
      name: string;
      email: string;
      role?: string;
      status?: string;
      permissions?: string | UserPermissions;
      avatar?: string;
      organization_ids?: string;
      createdAt?: string;
    };
    organizations?: Array<{ id: string; name: string; slug: string }>;
  }): AuthAdapterResponse {
    const role = normalizeRole(data.user.role, 'Dispatcher');
    const organizations = parseOrganizations(data.user.organization_ids, role, data.organizations);

    // Check user account status
    if (!isUserActive(data.user.status)) {
      throw new Error('Account is not active. Please contact your administrator.');
    }

    // Handle permissions - could be string or object from adapter
    const permissionsInput =
      typeof data.user.permissions === 'string'
        ? data.user.permissions
        : JSON.stringify(data.user.permissions);

    const user: User = {
      id: data.user.id,
      name: data.user.name,
      email: data.user.email,
      role,
      permissions: extractPermissions(permissionsInput, role),
      avatar: data.user.avatar,
      organizations: organizations.length > 0 ? organizations : undefined,
      created_at: data.user.createdAt,
    };

    return {
      user,
      tokens: {
        accessToken: data.token,
        refreshToken: data.refresh_token,
        expiresIn: data.expires_in,
        expiresAt: Date.now() + data.expires_in * 1000,
      },
      organizations,
    };
  }

  /**
   * Normalize driver login response
   * Response format: { token, refresh_token, expires_in, driver: {...} }
   */
  private normalizeDriverLoginResponse(data: {
    token: string;
    refresh_token: string;
    expires_in: number;
    driver: {
      id: string;
      name: string;
      email: string;
      phone?: string;
      status?: string;
      rating?: number;
    };
    organizations?: Array<{ id: string; name: string; slug: string }>;
  }): AuthAdapterResponse {
    const role: UserRole = 'Driver';

    // Note: data.driver.status is the operational availability status (idle/busy/offline),
    // NOT the account active/inactive status. If the backend authenticated the driver,
    // their account is active — deactivated accounts are rejected at the auth layer.

    const organizations: Organization[] = (data.organizations || []).map((org) => ({
      id: org.id,
      name: org.name,
      role,
    }));

    const user: User = {
      id: data.driver.id,
      name: data.driver.name,
      email: data.driver.email,
      role,
      permissions: { ...DEFAULT_PERMISSIONS },
      organizations: organizations.length > 0 ? organizations : undefined,
    };

    return {
      user,
      tokens: {
        accessToken: data.token,
        refreshToken: data.refresh_token,
        expiresIn: data.expires_in,
        expiresAt: Date.now() + data.expires_in * 1000,
      },
      organizations,
    };
  }

  /**
   * Normalize customer login response
   * Response format: { token, refresh_token, expires_in, customer: {...} }
   */
  private normalizeCustomerLoginResponse(data: {
    token: string;
    refresh_token: string;
    expires_in: number;
    customer: {
      id: string;
      name: string;
      email: string;
      company?: string;
      phone?: string;
      avatar?: string;
    };
    organizations?: Array<{ id: string; name: string; slug: string }>;
  }): AuthAdapterResponse {
    const role: UserRole = 'Customer';

    const organizations: Organization[] = (data.organizations || []).map((org) => ({
      id: org.id,
      name: org.name,
      role,
    }));

    const user: User = {
      id: data.customer.id,
      name: data.customer.name,
      email: data.customer.email,
      role,
      permissions: { ...DEFAULT_PERMISSIONS },
      avatar: data.customer.avatar,
      organizations: organizations.length > 0 ? organizations : undefined,
    };

    return {
      user,
      tokens: {
        accessToken: data.token,
        refreshToken: data.refresh_token,
        expiresIn: data.expires_in,
        expiresAt: Date.now() + data.expires_in * 1000,
      },
      organizations,
    };
  }

  /**
   * Normalize user from session response for internal use
   */
  private normalizeUser(user: SessionResponse['user']): User {
    const role = normalizeRole(user.role, 'Dispatcher');
    const organizations = parseOrganizations(user.organization_ids, role);

    return {
      id: user.id,
      name: user.name,
      email: user.email,
      role,
      permissions: extractPermissions(user.permissions, role),
      avatar: user.image,
      organizations: organizations.length > 0 ? organizations : undefined,
      created_at: user.createdAt,
    };
  }
}
